// Copyright 2020 Drivetrain Hub LLC
// Part geometry functions for three.js.

// eslint-disable-next-line no-unused-vars
import {Object3D, Shape, BufferGeometry, ExtrudeBufferGeometry, SphereBufferGeometry} from 'three';
import {BufferGeometryUtils} from 'three/examples/jsm/utils/BufferGeometryUtils';
import {OBJExporter} from "three/examples/jsm/exporters/OBJExporter";
import {STLExporter} from 'three/examples/jsm/exporters/STLExporter.js';
import * as THREE from 'three';
import defaults from "../../../config/defaults";
import handlers from "../../../handlers";
import gear from "../gear";
import {mirrorAboutX, points, rotatePoint2, rotationMatrix2} from "../coord";
import {computeBufferCylinderNormals, sweepBufferGeometryHelix} from "./helper";
import {store} from "../../../store";

// region GEARS

/**
 * Extrude transverse shape along a straight or swept helical path.
 * @param {Shape} shape - THREE.Shape object of transverse section to extrude.
 * @param {number} depth - Depth to extrude.
 * @param {number} helixRadius - Helix radius corresponding to the specified helix angle.
 * @param {number} helixAngle - Helix angle at the specified helix radius, [rad].
 * @param {number} diameterCylinder - Diameter of cylindrical surface to apply smoothing of vertex normals.
 * @return {ExtrudeBufferGeometry} - THREE.ExtrudeBufferGeometry object.
 */
function extrudeGear(shape, depth, helixRadius, helixAngle, diameterCylinder = 0) {
  let geometry;

  if (helixAngle === 0) {
    // straight extrusion
    const extrudeSettings = {
      depth: depth,
      steps: 1,  // depth discretization
      curveSegments: 12,  // discretized segments of each curve in cross section shape; default=12
      bevelEnabled: false,
    };
    geometry = new THREE.ExtrudeBufferGeometry(shape, extrudeSettings);

    // smooth shading on cylinder surface
    computeBufferCylinderNormals(geometry, 2 * helixRadius, diameterCylinder);
  } else {
    // helical sweep
    geometry = sweepBufferGeometryHelix(shape, depth, helixRadius, helixAngle, diameterCylinder);

    // orient tooth #1 on x-axis in the datum cross-section
    const {phi} = gear.helixRotation(depth / 2, helixAngle, helixRadius);
    geometry.rotateZ(-phi);
  }

  // set datum to center of depth
  geometry.translate(0, 0, -depth / 2);

  return geometry;
}

/**
 * Draw 3D external gear for spur and helical gears.
 * Teeth may have undercut and a protuberance involute.
 * Gear is oriented with tooth #1 centered on x-axis.
 * Origin is at the center of the rotational gear body at mid-facewidth.
 * Expects root fillet coordinates for right-tooth, with the tooth root centered on x-axis.
 * @param {object} - Instance of GraphQL GearDetailGeom type.
 * @param {object} - Instance of GraphQL GearTooth root params.
 * @return {ExtrudeBufferGeometry} Geometry object for use with three.js.
 */
export function threeGearExternal(
  {moduleNormal, pressureAngleNormal, numberOfTeeth, helixAngle, facewidth, tipDiameter, rimDiameter, toothThicknessNormalFinish},
  {x: xFilletR, y: yFilletR, filletBoundaryDiameter, protuberanceBoundaryDiameter, protuberanceBaseDiameter}) {

  // region CONFIG

  const nFillet = xFilletR.length;
  const isFullRoot = Math.abs(yFilletR[0]) < 1e-10;

  const hasStandardInvolute = protuberanceBoundaryDiameter < tipDiameter;
  const hasProtuberanceInvolute = filletBoundaryDiameter !== protuberanceBoundaryDiameter;
  const protuberanceBaseRadius = protuberanceBaseDiameter / 2;

  // DEV ONLY
  // console.log('three.js external gear');
  // console.log('moduleNormal', moduleNormal);
  // console.log('pressureAngleNormal', pressureAngleNormal);
  // console.log('numberOfTeeth', numberOfTeeth);
  // console.log('helixAngle', helixAngle);
  // console.log('facewidth', facewidth);
  // console.log('tipDiameter', tipDiameter);
  // console.log('rimDiameter', rimDiameter);
  // console.log('toothThicknessNormalFinish', toothThicknessNormalFinish);

  // endregion

  // region PARAMS

  // calculate base radius
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const baseRadius = baseDiameter / 2;

  // calculate roll angles of profile involute (not protuberance involute)
  const rollAngleInvStart = gear.diameter2RollAngle(baseDiameter, protuberanceBoundaryDiameter);
  const rollAngleInvEnd = gear.diameter2RollAngle(baseDiameter, tipDiameter);

  // calculate angular tooth thickness and pitch
  const toothThicknessTransverse = gear.toothThicknessTransverse1({toothThicknessNormal: toothThicknessNormalFinish, helixAngle});
  const toothBaseHalfAngle = gear.toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse});
  const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);

  // DEV ONLY
  // function rad2deg(a) {
  //     return a * 180 / Math.PI;
  // }
  // console.log(diameterReference, rad2deg(pressureAngleTransverse), diameterBase);
  // console.log(diameterForm, rad2deg(rollAngleStart), rad2deg(rollAngleEnd));
  // console.log(toothThicknessNormal, toothThicknessTransverse, rad2deg(toothBaseHalfAngle), rad2deg(pitchAngleTransverse));
  // console.log(pitchAngleTransverse * diameterReference / 2);

  // endregion

  // region TRANSVERSE SHAPE

  let gearShape = new THREE.Shape();
  ( function drawGearShape(ctx) {
    // region CONTROL POINTS

    // involute Bezier control points: RIGHT and LEFT profiles
    let rInvPoints = gear.involuteBezierCoeffs(baseRadius, rollAngleInvStart, rollAngleInvEnd, 3);
    let lInvPoints = mirrorAboutX(rInvPoints);

    // root fillet points
    let rFillet = points(xFilletR, yFilletR);
    let lFillet = mirrorAboutX(rFillet).reverse();

    // rotation matrices
    const rotPitch = rotationMatrix2(pitchAngleTransverse);
    const rotHalfPitch = rotationMatrix2(pitchAngleTransverse / 2);
    const rotBaseHalfAngleMinus = rotationMatrix2(-toothBaseHalfAngle);
    const rotBaseHalfAnglePlus = rotationMatrix2(toothBaseHalfAngle);

    // rotate: RIGHT profile by -0.5 tooth thickness, LEFT profile by +0.5 tooth thickness
    let [rInv0, rInv1, rInv2, rInv3] = rInvPoints.map(p => rotatePoint2(rotBaseHalfAngleMinus, p));
    let [lInv0, lInv1, lInv2, lInv3] = lInvPoints.map(p => rotatePoint2(rotBaseHalfAnglePlus, p));

    // rotate: BOTH root fillets by +0.5 pitch (to draw them successively)
    rFillet = rFillet.map(p => rotatePoint2(rotHalfPitch, p));
    lFillet = lFillet.map(p => rotatePoint2(rotHalfPitch, p));

    // endregion

    // region PROTUBERANCE [OPTIONAL]
    let rProt0, rProt1, rProt2, rProt3;
    let lProt0, lProt1, lProt2, lProt3;

    if (hasProtuberanceInvolute) {
      // DEV ONLY
      // console.log('filletBoundaryDiameter', filletBoundaryDiameter);
      // console.log('root curve boundary dia', 2 * Math.hypot(xFilletR[nRoot-1], yFilletR[nRoot-1]));

      // calculate roll angles of protuberance involute
      const rollAngleProtubStart = gear.diameter2RollAngle(protuberanceBaseDiameter, filletBoundaryDiameter);
      const rollAngleProtubEnd = gear.diameter2RollAngle(protuberanceBaseDiameter, protuberanceBoundaryDiameter);

      // involute Bezier control points: left and right protuberance
      let rProtPoints = gear.involuteBezierCoeffs(protuberanceBaseRadius, rollAngleProtubStart, rollAngleProtubEnd, 3);
      let lProtPoints = mirrorAboutX(rProtPoints);

      // polar angle correction of protuberance involute
      const rProtFilletPt = rProtPoints[0];
      const phiInvStart = Math.atan2(rProtFilletPt.y, rProtFilletPt.x);
      const filletToothSpaceHalfAngle = Math.atan2(yFilletR[nFillet-1], xFilletR[nFillet-1]);
      const phiErr = filletToothSpaceHalfAngle - phiInvStart;

      // rotation matrices for protuberance involutes
      const rotProtubR = rotationMatrix2(phiErr + pitchAngleTransverse / 2);
      const rotProtubL = rotationMatrix2(-phiErr + pitchAngleTransverse / 2);

      // rotate protuberance involutes
      [rProt0, rProt1, rProt2, rProt3] = rProtPoints.map(p => rotatePoint2(rotProtubR, p));
      [lProt0, lProt1, lProt2, lProt3] = lProtPoints.map(p => rotatePoint2(rotProtubL, p));

      // DEV ONLY - checking coincidence
      // WARNING - some error will exist due to Bezier curve approximations
      // console.log('rProt0', rProt0);
      // console.log('rFillet[-1]', rFillet[nRoot-1]);
    }

    // endregion

    // region TOOTH CURVES

    // move to start of right involute
    ctx.moveTo(rInv0.x, rInv0.y);

    // PLACEHOLDER {performance} remove if-conditions from loop; make separate loops for separate conditions
    for (let i=0; i < numberOfTeeth; i++) {
      if (hasStandardInvolute) {
        // draw right involute curve from root-to-tip
        ctx.bezierCurveTo(rInv1.x, rInv1.y, rInv2.x, rInv2.y, rInv3.x, rInv3.y);

        // draw tip land from right-to-left flank; line instead of arc to limit model fidelity
        ctx.lineTo(lInv3.x, lInv3.y);

        // draw left involute curve from tip-to-root; connects to left root fillet or protuberance involute
        ctx.bezierCurveTo(lInv2.x, lInv2.y, lInv1.x, lInv1.y, lInv0.x, lInv0.y);
      } else {
        // draw tip arc from right-to-left protuberance involute; line instead of arc to limit model fidelity
        ctx.lineTo(lProt3.x, lProt3.y);
      }

      if (hasProtuberanceInvolute) {
        // draw left protuberance involute curve from tip-to-root; connects to left root fillet
        // lProt3 coincident with lInv0, hence why a separate curve is not drawing from lInv0 --> lProt3
        // WARNING: Some error (Bezier) will exist between lProt0 and lFillet[0].
        ctx.bezierCurveTo(lProt2.x, lProt2.y, lProt1.x, lProt1.y, lProt0.x, lProt0.y);

        // DEV ONLY - checking coincidence
        // WARNING - some error will exist due to Bezier curve approximations
        // console.log('lInv0', lInv0);
        // console.log('lProt3', lProt3);
        // console.log('---')
        // console.log('lProt0', lProt0);
        // console.log('lFillet[0]', lFillet[0]);
        // console.log('---')
        // console.log('rProt0', rProt0);
        // console.log('rFillet[-1]', rFillet[nRoot-1]);
        // console.log('===');
      }

      // draw left fillet curve; connects to start of root arc
      // slice(1) used because lInv0 or lProt0 coincident with lFillet[0]
      // WARNING: ctx.splineThru caused performance issues when updating parts; three.js r98
      // ctx.splineThru(lFillet.slice(1).map(p => new THREE.Vector2(p.x, p.y)));
      lFillet.slice(1, nFillet).forEach(p => ctx.lineTo(p.x, p.y));

      // draw root arc from left-to-right fillet; approximated as line
      if (!isFullRoot) {
        const rFilletStart = rFillet[0];
        ctx.lineTo(rFilletStart.x, rFilletStart.y)
      }

      // draw right fillet curve; connects to start of protuberance/involute
      // slice(1) used because rFillet[0] is coincident with rProt0/rInv0
      // WARNING: ctx.splineThru caused performance issues when updating parts; three.js r98
      // ctx.splineThru(rFillet.slice(1).map(p => new THREE.Vector2(p.x, p.y)));
      rFillet.slice(1, nFillet).forEach(p => ctx.lineTo(p.x, p.y));

      // rotate control points of profile involutes
      [rInv0, rInv1, rInv2, rInv3] = [rInv0, rInv1, rInv2, rInv3].map(p => rotatePoint2(rotPitch, p));
      [lInv0, lInv1, lInv2, lInv3] = [lInv0, lInv1, lInv2, lInv3].map(p => rotatePoint2(rotPitch, p));

      if (hasProtuberanceInvolute) {
        // draw right protuberance involute from root-to-tip; connects to right involute
        // rProt3 coincident with rInv0, hence why a separate curve is not drawing from rProt3 --> rInv0
        ctx.bezierCurveTo(rProt1.x, rProt1.y, rProt2.x, rProt2.y, rProt3.x, rProt3.y);

        // rotate control points of protuberance involutes
        [rProt0, rProt1, rProt2, rProt3] = [rProt0, rProt1, rProt2, rProt3].map(p => rotatePoint2(rotPitch, p));
        [lProt0, lProt1, lProt2, lProt3] = [lProt0, lProt1, lProt2, lProt3].map(p => rotatePoint2(rotPitch, p));
      }

      // rotate coordinates of root fillets
      rFillet = rFillet.map(p => rotatePoint2(rotPitch, p));
      lFillet = lFillet.map(p => rotatePoint2(rotPitch, p));
    }

    // endregion
  })(gearShape);

  // add bore to shape; curve points per segment is set in extrudeSettings
  if (rimDiameter) {
    let borePath = new THREE.Path();
    borePath.absarc(0, 0, rimDiameter / 2, 0, 2 * Math.PI);
    gearShape.holes.push(borePath);
  }

  // endregion

  return extrudeGear(gearShape, facewidth, theoreticalPitchDiameter / 2, helixAngle, rimDiameter);
}

/**
 * Draw 3D internal gear for spur and helical gears.
 * Teeth with a protuberance involute are not supported.
 * Gear is oriented with tooth #1 centered on x-axis.
 * Origin is at the center of the rotational gear body at mid-facewidth.
 * Expects root fillet coordinates for right-tooth, with the tooth root centered on x-axis.
 * @param {object} - Instance of GraphQL GearDetailGeom type.
 * @param {object} - Instance of GraphQL GearTooth root params.
 * @return {ExtrudeBufferGeometry} Geometry object for use with three.js.
 */
export function threeGearInternal(
  {moduleNormal, pressureAngleNormal, numberOfTeeth, helixAngle, facewidth, tipDiameter, rimDiameter, toothThicknessNormalFinish},
  {x: xFilletR, y: yFilletR, filletBoundaryDiameter}) {

  // region CONFIG

  const nFillet = xFilletR.length;
  const isFullRoot = Math.abs(yFilletR[0]) < 1e-10;
  const numberOfTeethPositive = -numberOfTeeth;

  // endregion

  // region PARAMS

  // calculate base radius
  const moduleTransverse = gear.moduleTransverse1({moduleNormal, helixAngle});
  const theoreticalPitchDiameter = gear.theoreticalPitchDiameter1({numberOfTeeth, moduleTransverse});
  const pressureAngleTransverse = gear.pressureAngleTransverse1({pressureAngleNormal, helixAngle});
  const baseDiameter = gear.baseDiameter1({theoreticalPitchDiameter, pressureAngleTransverse});
  const baseRadius = baseDiameter / 2;

  // calculate roll angles of profile involute
  const rollAngleStart = gear.diameter2RollAngle(baseDiameter, filletBoundaryDiameter);
  const rollAngleEnd = gear.diameter2RollAngle(baseDiameter, tipDiameter);

  // calculate angular tooth thickness and pitch
  const toothThicknessTransverse = gear.toothThicknessTransverse1({toothThicknessNormal: toothThicknessNormalFinish, helixAngle});
  const toothBaseHalfAngle = gear.toothBaseHalfAngleTransverse1({theoreticalPitchDiameter, pressureAngleTransverse, toothThicknessTransverse});
  const pitchAngleTransverse = gear.pitchAngleTransverse1(numberOfTeeth);

  // DEV ONLY
  // console.log('--- TOOTH PARAMS ---');
  // console.log('helixAngle', helixAngle);
  // console.log('moduleNormal', moduleNormal);
  // console.log('moduleTransverse', moduleTransverse);
  // console.log('pressureAngleNormal', pressureAngleNormal);
  // console.log('pressureAngleTransverse', pressureAngleTransverse);
  // console.log('theoreticalPitchDiameter', theoreticalPitchDiameter);
  // console.log('tipDiameter', tipDiameter);
  // console.log('rimDiameter', rimDiameter);
  // console.log('baseDiameter', baseDiameter);
  // console.log('rollAngleStart', rollAngleStart);
  // console.log('rollAngleEnd', rollAngleEnd);
  // console.log('toothThicknessNormalFinish', toothThicknessNormalFinish);
  // console.log('toothThicknessTransverse', toothThicknessTransverse);
  // console.log('toothBaseHalfAngle', toothBaseHalfAngle);
  // console.log('pitchAngleTransverse', pitchAngleTransverse);
  // console.log('--- ROOT PARAMS ---');
  // console.log('filletBoundaryDiameter', filletBoundaryDiameter);
  // console.log('protuberanceBoundaryDiameter', protuberanceBoundaryDiameter);
  // console.log('protuberanceBaseDiameter', protuberanceBaseDiameter);
  // console.log('xFilletR', xFilletR);
  // console.log('yFilletR', yFilletR);

  // endregion

  // region TRANSVERSE SHAPE

  // path of outside rim diameter
  let gearShape = new THREE.Shape();
  const nSegments = 2;  // curve points per segment is set in extrudeSettings
  const segmentRange = 2 * Math.PI / nSegments;

  for (let i=0; i < nSegments; i++) {
    const segmentStart = i * segmentRange;
    const segmentEnd = segmentStart + segmentRange;
    gearShape.absarc(0, 0, rimDiameter / 2, segmentStart, segmentEnd);
  }

  // path of transverse gear teeth
  let gearPath = new THREE.Path();
  ( function drawGearPath(ctx) {
    // region CONTROL POINTS

    // involute Bezier control points: left and right profiles
    let rInvPoints = gear.involuteBezierCoeffs(baseRadius, rollAngleStart, rollAngleEnd, 3);
    let lInvPoints = mirrorAboutX(rInvPoints);

    // flip fillet x-coord to work with involutes derived from (-) diameters
    xFilletR = xFilletR.map(x => -x);

    // root fillet points
    let rFillet = points(xFilletR, yFilletR);
    let lFillet = mirrorAboutX(rFillet).reverse();

    // DEV ONLY
    // console.log('rInvPoints', rInvPoints);
    // console.log('lInvPoints', lInvPoints);
    // console.log('rFillet', rFillet);
    // console.log('lFillet', lFillet);
    // console.log('pitchAngleTransverse', pitchAngleTransverse);
    // console.log('toothBaseHalfAngle', toothBaseHalfAngle);

    // rotation matrices of first tooth; configured to center it on x-axis
    const rotHalfPitchStart = rotationMatrix2(pitchAngleTransverse / 2);
    const rotBaseHalfAngleMinusStart = rotationMatrix2(-toothBaseHalfAngle);
    const rotBaseHalfAnglePlusStart = rotationMatrix2(toothBaseHalfAngle);

    // rotate: RIGHT profile by -0.5 tooth thickness, LEFT profile by +0.5 tooth thickness
    let [rInv0, rInv1, rInv2, rInv3] = rInvPoints.map(p => rotatePoint2(rotBaseHalfAngleMinusStart, p));
    let [lInv0, lInv1, lInv2, lInv3] = lInvPoints.map(p => rotatePoint2(rotBaseHalfAnglePlusStart, p));

    // rotate: BOTH root fillets by +0.5 pitch (to draw them successively)
    rFillet = rFillet.map(p => rotatePoint2(rotHalfPitchStart, p));
    lFillet = lFillet.map(p => rotatePoint2(rotHalfPitchStart, p));

    // DEV ONLY
    // console.log('[rInv0, rInv1, rInv2, rInv3]', [rInv0, rInv1, rInv2, rInv3]);
    // console.log('[lInv0, lInv1, lInv2, lInv3]', [lInv0, lInv1, lInv2, lInv3]);

    // endregion

    // region TOOTH CURVES

    // move to start of right involute
    ctx.moveTo(rInv0.x, rInv0.y);
    const rotPitch = rotationMatrix2(pitchAngleTransverse);

    // draw path for each tooth
    for (let i=0; i < numberOfTeethPositive; i++) {
      // draw right involute curve from root-to-tip
      ctx.bezierCurveTo(rInv1.x, rInv1.y, rInv2.x, rInv2.y, rInv3.x, rInv3.y);

      // draw tip arc from right-to-left flank; approximated as line
      ctx.lineTo(lInv3.x, lInv3.y);

      // draw left involute curve from tip-to-root; connects to root fillet
      ctx.bezierCurveTo(lInv2.x, lInv2.y, lInv1.x, lInv1.y, lInv0.x, lInv0.y);

      // draw root fillet of left flank; connects to start of root arc
      // slice(1) used because lInv0 is coincident with lFillet[0]
      // WARNING: ctx.splineThru caused performance issues when updating parts; three.js r98
      // ctx.splineThru(lFillet.slice(1).map(p => new THREE.Vector2(p.x, p.y)));
      lFillet.slice(1, nFillet).forEach(p => ctx.lineTo(p.x, p.y));

      // draw root arc from left-to-right fillet; approximated as line
      if (!isFullRoot) {
        const rFilletStart = rFillet[0];
        ctx.lineTo(rFilletStart.x, rFilletStart.y)
      }

      // draw right fillet curve; connects to start of right involute
      // slice(1) used because rFillet[0] is coincident with rInv0
      // WARNING: ctx.splineThru caused performance issues when updating parts; three.js r98
      // ctx.splineThru(rFillet.slice(1).map(p => new THREE.Vector2(p.x, p.y)));
      rFillet.slice(1, nFillet).forEach(p => ctx.lineTo(p.x, p.y));

      // rotate control points of profile involutes
      [rInv0, rInv1, rInv2, rInv3] = [rInv0, rInv1, rInv2, rInv3].map(p => rotatePoint2(rotPitch, p));
      [lInv0, lInv1, lInv2, lInv3] = [lInv0, lInv1, lInv2, lInv3].map(p => rotatePoint2(rotPitch, p));

      // rotate coordinates of root fillets
      rFillet = rFillet.map(p => rotatePoint2(rotPitch, p));
      lFillet = lFillet.map(p => rotatePoint2(rotPitch, p));
    }

    // endregion
  })(gearPath);

  gearShape.holes.push(gearPath);

  // endregion

  // invert scaling to center tooth #1 on +X-axis
  const helixRadius = Math.abs(theoreticalPitchDiameter / 2);
  let gearGeometry = extrudeGear(gearShape, facewidth, helixRadius, helixAngle, -rimDiameter);
  gearGeometry.scale(-1, -1, 1);

  return gearGeometry;
}

// endregion

// region FRAMES

const CURVE_SEGMENTS = 16;
const NODE_DIA_SCALE = 0.125;
const datumRadiusScale = (datumSize) => Math.hypot(datumSize, datumSize, datumSize) / 2;

/**
 * Sphere geometry based on mean part diameter.
 * @return {SphereBufferGeometry} Geometry object for use with three.js.
 */
function threeMeanSphere() {
  const nodeSize = store.state.ui.size.diameterMin * NODE_DIA_SCALE;
  const sphereRadius = datumRadiusScale(nodeSize);
  return new THREE.SphereBufferGeometry(sphereRadius, CURVE_SEGMENTS, CURVE_SEGMENTS);
}

/**
 * Shaft frame geometry.
 * Draws slender tube with a box at each mounted part, scaled based on diametral size of parts.
 * @param {[object]} parts - Array of GraphQL Part objects.
 * @return {BufferGeometry} Geometry object for use with three.js.
 */
export function threeFrameShaft(parts) {
  // if no parts connected, draw a mean sphere
  if (parts.length === 0) return threeMeanSphere();

  // region PARAMS

  let zNodes=[], diameters=[], diameter;

  parts.forEach(p => {
    ({diameter} = handlers.model.partSize(p));
    diameters.push(diameter);
    zNodes.push(p.pos[2]);
  });

  // shaft geometry
  const zNodesAndDatum = [...zNodes, 0];
  const shaftStart = Math.min(...zNodesAndDatum);
  const shaftEnd = Math.max(...zNodesAndDatum);
  const shaftLength = (shaftEnd - shaftStart);
  const nodeSize = Math.min(...diameters.filter(d => d !== 0)) * NODE_DIA_SCALE;
  const datumRadius = datumRadiusScale(nodeSize);
  const shaftRadius = nodeSize / 2;

  // endregion

  // region GEOMETRY

  let cylinder = new THREE.CylinderBufferGeometry(shaftRadius, shaftRadius, shaftLength, CURVE_SEGMENTS);

  // orients cylinder along +Z; default is +Y
  cylinder.lookAt(new THREE.Vector3(0, 1, 0));

  // set shaft datum; default cylinder datum is at its center
  cylinder.translate(0, 0, shaftLength / 2 + shaftStart);

  // define each node box
  let node, nodes = [];
  zNodes.forEach(z => {
    node = new THREE.BoxBufferGeometry(nodeSize, nodeSize, nodeSize);
    node.translate(0, 0, z);
    nodes.push(node);
  });

  // define sphere for shaft datum @ (0, 0, 0)
  nodes.push(new THREE.SphereBufferGeometry(datumRadius, CURVE_SEGMENTS, CURVE_SEGMENTS));

  // endregion

  return BufferGeometryUtils.mergeBufferGeometries([cylinder, ...nodes], true);
}

/**
 * Carrier frame geometry.
 * @param {object} - Instance of GraphQL FrameGeom type.
 * @param {[object]} parts - Array of GraphQL Part objects.
 * @return {BufferGeometry} Geometry object for use with three.js.
 */
export function threeFrameCarrier({pitchCircleDiameter}, parts) {
  // References:
  //  1. https://threejs.org/examples/#webgl_geometry_shapes
  //  2. https://github.com/mrdoob/three.js/blob/master/examples/webgl_geometry_shapes.html

  // FORGONE - adjust extrusion depth based on position of parts
  // const zCoord = parts.map(p => p.pos[2]);
  // const zRange = Math.max(...zCoord) - Math.min(...zCoord);
  // const depthMin = defaults.geom.carrier.aspectRatio * pitchCircleDiameter;
  // const depth = zRange > depthMin ? zRange : depthMin;
  // const zOffset = zRange > 0 ? -zRange : -depth / 2;

  // region EXTRUDE - SHAPE

  const endAngle = Math.PI * 2;
  const pitchRadius = pitchCircleDiameter / 2;
  const deltaRadius = pitchRadius * defaults.geom.carrier.deltaDiameter;
  const outerRadius = pitchRadius + deltaRadius;
  const innerRadius = pitchRadius - deltaRadius;

  let frameShape = new THREE.Shape();
  frameShape.absarc(0, 0, outerRadius, 0, endAngle);

  let holePath = new THREE.Path();
  holePath.absarc(0, 0, innerRadius, 0, endAngle);

  frameShape.holes.push(holePath);

  // endregion

  // region EXTRUDE - GEOMETRY

  const extrudeSettings = {
    depth: defaults.geom.carrier.aspectRatio * pitchCircleDiameter,
    steps: 1,  // depth discretization
    curveSegments: 24,  // discretized segments of each curve in cross section shape; default=12
    bevelEnabled: false,
  };
  let ringGeometry = new THREE.ExtrudeBufferGeometry(frameShape, extrudeSettings);

  // smooth shading on cylindrical surfaces; also tried geometry.normalizeNormals and .computeVertexNormals
  computeBufferCylinderNormals(ringGeometry, pitchCircleDiameter, 2 * outerRadius);
  computeBufferCylinderNormals(ringGeometry, pitchCircleDiameter, 2 * innerRadius);

  // set datum to center of extrusion depth
  ringGeometry.translate(0, 0, -extrudeSettings.depth / 2);

  // endregion

  // region NODES

  // FORGONE - node size based on min part diameter
  // let diameter;
  // let diameters = parts.map(p => {
  //   ({diameter} = handlers.model.partSize(p));
  //   return diameter;
  // });
  // const nodeSize = Math.min(...diameters.filter(d => d !== 0)) * NODE_DIA_SCALE;

  const nodeSize = 2 * deltaRadius;
  const nodeHeight = extrudeSettings.depth;

  // define each node cylinder; extrude geometry is non-indexed, hence the method call here
  let x, y, node, nodes = [];
  parts.forEach(part => {
    [x, y] = part.pos;
    node = new THREE.CylinderBufferGeometry(nodeSize, nodeSize, nodeHeight, 24).toNonIndexed();
    node.lookAt(new THREE.Vector3(0, 1, 0));  // orient cylinder along +Z; default is +Y
    node.translate(x, y, 0);  // z=0 to keep node block inline with ring
    nodes.push(node);
  });

  // endregion

  return BufferGeometryUtils.mergeBufferGeometries([ringGeometry, ...nodes], true);
}

// endregion

// region EXPORTS

/**
 * Export a three.js object to a tessellated data format.
 * @param {Object3D} object3 - THREE.Object3D object to export.
 * @param {string} format - File format for tessellation; 'stl' or 'obj'.
 * @return {string} Tessellated part data in the specified format.
 */
export function exportTessellation(object3, format) {
  let data;

  switch (format.toLowerCase()) {
    case 'obj': {
      let exporter = new OBJExporter();
      data = exporter.parse(object3);
      break;
    }
    case 'stl': {
      let exporter = new STLExporter();
      data = exporter.parse(object3, {binary: true});
      break;
    }
    default:
      throw Error('Invalid file format for tessellation.')
  }

  return data;
}

// endregion
